/**
 * ComplianceTagChips Component
 *
 * Displays compliance tags as chips with edit capability.
 * Used in the lakehouse catalog for tagging (FR-204).
 *
 * Requirements:
 * - FR-204: Tag lakehouses with compliance requirements
 * - Constitution v2.0.0: Policy-driven classification
 */

import React, { useState } from 'react';

export interface ComplianceTagChipsProps {
  tags: string[];
  editable?: boolean;
  onTagsChange?: (tags: string[]) => void;
  onRemove?: (tag: string) => void; // Direct remove callback
  maxDisplay?: number;
  size?: 'sm' | 'md';
}

/**
 * Predefined compliance tags available for assignment
 */
export const PREDEFINED_TAGS = [
  'PII',
  'HIPAA',
  'GDPR',
  'SOX',
  'PCI',
  'Financial',
  'Critical',
  'General',
] as const;

/**
 * Predefined compliance tag colors
 */
const tagColors: Record<string, { bg: string; text: string; border: string }> = {
  PII: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' },
  HIPAA: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' },
  GDPR: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
  SOX: { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-200' },
  PCI: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200' },
  Financial: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' },
  Critical: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' },
  General: { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200' },
};

const defaultColor = { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200' };

/**
 * Size classes
 */
const sizeClasses = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
};

/**
 * Single tag chip
 */
const TagChip: React.FC<{
  tag: string;
  size?: 'sm' | 'md';
  onRemove?: () => void;
}> = ({ tag, size = 'md', onRemove }) => {
  const colors = tagColors[tag] || defaultColor;

  return (
    <span
      className={`
        inline-flex items-center font-medium rounded-full border
        ${colors.bg} ${colors.text} ${colors.border}
        ${sizeClasses[size]}
      `}
    >
      {tag}
      {onRemove && (
        <button
          type='button'
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className='ml-1 -mr-1 hover:text-red-600 transition-colors'
          title={`Remove ${tag}`}
        >
          <svg className='w-3 h-3' fill='currentColor' viewBox='0 0 20 20'>
            <path
              fillRule='evenodd'
              d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z'
              clipRule='evenodd'
            />
          </svg>
        </button>
      )}
    </span>
  );
};

/**
 * ComplianceTagChips Component
 */
export const ComplianceTagChips: React.FC<ComplianceTagChipsProps> = ({
  tags,
  editable = false,
  onTagsChange,
  onRemove,
  maxDisplay = 5,
  size = 'md',
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [newTag, setNewTag] = useState('');

  // Handle undefined tags gracefully
  const safeTags = tags || [];
  const displayTags = safeTags.slice(0, maxDisplay);
  const hiddenCount = Math.max(0, safeTags.length - maxDisplay);

  const handleRemove = (tagToRemove: string) => {
    // Use direct onRemove callback if provided
    if (onRemove) {
      onRemove(tagToRemove);
    } else if (onTagsChange) {
      onTagsChange(safeTags.filter((t) => t !== tagToRemove));
    }
  };

  const handleAdd = () => {
    if (newTag.trim() && !safeTags.includes(newTag.trim())) {
      if (onTagsChange) {
        onTagsChange([...safeTags, newTag.trim()]);
      }
      setNewTag('');
      setIsAdding(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    } else if (e.key === 'Escape') {
      setIsAdding(false);
      setNewTag('');
    }
  };

  if (safeTags.length === 0 && !editable) {
    return <span className='text-sm text-[var(--color-text-secondary)]'>No tags</span>;
  }

  return (
    <div className='flex flex-wrap items-center gap-1'>
      {displayTags.map((tag) => (
        <TagChip
          key={tag}
          tag={tag}
          size={size}
          onRemove={editable ? () => handleRemove(tag) : undefined}
        />
      ))}

      {hiddenCount > 0 && (
        <span
          className={`${sizeClasses[size]} text-[var(--color-text-secondary)] cursor-pointer hover:text-[var(--color-text-primary)]`}
          title={safeTags.slice(maxDisplay).join(', ')}
        >
          +{hiddenCount} more
        </span>
      )}

      {editable && !isAdding && (
        <button
          type='button'
          onClick={() => setIsAdding(true)}
          className={`
            inline-flex items-center justify-center rounded-full border border-dashed
            border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[var(--color-accent)] hover:text-[var(--color-accent)]
            transition-colors ${sizeClasses[size]}
          `}
          title='Add tag'
        >
          <svg className='w-3 h-3 mr-1' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 4v16m8-8H4' />
          </svg>
          Add
        </button>
      )}

      {isAdding && (
        <div className='flex items-center'>
          <input
            type='text'
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => {
              if (!newTag.trim()) {
                setIsAdding(false);
              }
            }}
            className='w-24 px-2 py-1 text-sm border border-[var(--color-accent)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] rounded focus:outline-none focus:ring-1 focus:ring-[var(--color-accent)]'
            placeholder='Tag name'
            autoFocus
          />
          <button
            type='button'
            onClick={handleAdd}
            className='ml-1 p-1 text-green-600 hover:text-green-700'
            title='Add'
          >
            <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M5 13l4 4L19 7'
              />
            </svg>
          </button>
          <button
            type='button'
            onClick={() => {
              setIsAdding(false);
              setNewTag('');
            }}
            className='ml-1 p-1 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
            title='Cancel'
          >
            <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M6 18L18 6M6 6l12 12'
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * Available tags list for suggestions
 */
export const availableComplianceTags = [
  'PII',
  'HIPAA',
  'GDPR',
  'SOX',
  'PCI',
  'Financial',
  'Critical',
  'General',
  'Internal',
  'Confidential',
  'Public',
];

export default ComplianceTagChips;
